import { useState, useEffect, useRef } from "react";
import { useStore, CAT_META } from "../store/Store";
import ProductCard from "../components/ProductCard";
import VideoProductCard from "../components/VideoProductCard";
import Footer from "../components/Footer";

export default function ShopPage({ onNav, initCat, searchQ }) {
  const { products, categories, catMetaExtra, videoShowcases } = useStore();
  const getMeta = (cat) => CAT_META[cat] || catMetaExtra[cat] || { emoji:"👗", color:"#EC4899", light:"#FDF2F8" };
  const [cat, setCat]     = useState(initCat||"All");
  const [search, setSearch] = useState(searchQ||"");
  const [sort, setSort]   = useState("Relevance");
  const [maxP, setMaxP]   = useState(15000);
  const [selSizes, setSelSizes] = useState([]);
  const [selColors, setSelColors] = useState([]);
  const [showF, setShowF] = useState(false);
  const [barHidden, setBarHidden] = useState(false);
  const lastScroll = useRef(0);

  useEffect(()=>{ if(initCat) setCat(initCat); },[initCat]);
  useEffect(()=>{ if(searchQ) setSearch(searchQ); },[searchQ]);

  // Search/sort bar tucks away on scroll-down (mobile only) and reappears on scroll-up —
  // same behavior as the top navbar.
  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth > 900) { setBarHidden(false); return; }
      const cur = window.scrollY;
      if (cur > lastScroll.current && cur > 140) setBarHidden(true);
      else setBarHidden(false);
      lastScroll.current = cur;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const allSizes = [...new Set(products.flatMap(p=>p.sizes||[]))];
  const allColors = [...new Set(products.flatMap(p=>p.colors||[]))];

  const filtered = products
    .filter(p=>cat==="All"||p.category===cat)
    .filter(p=>p.name?.toLowerCase().includes(search.toLowerCase())||p.category?.toLowerCase().includes(search.toLowerCase()))
    .filter(p=>p.price<=maxP)
    .filter(p=>selSizes.length===0 || (p.sizes||[]).some(s=>selSizes.includes(s)))
    .filter(p=>selColors.length===0 || (p.colors||[]).some(c=>selColors.includes(c)))
    .sort((a,b)=>sort==="Price: Low to High"?a.price-b.price:sort==="Price: High to Low"?b.price-a.price:sort==="Top Rated"?(b.rating||0)-(a.rating||0):0);

  const meta = cat!=="All" ? getMeta(cat) : null;
  const toggleFilter = (list, setList, val) => setList(l => l.includes(val) ? l.filter(x=>x!==val) : [...l, val]);

  return (
    <div className="page-top-pad" style={{ background:"white", minHeight:"100vh" }}>
      <div style={{ background:"linear-gradient(135deg,#1a1a2e,#2d1b69)", padding:"clamp(20px,4vw,36px) clamp(16px,4vw,40px)" }}>
        <div style={{ fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"#EC4899", marginBottom:6, fontWeight:700 }}>
          {meta?.emoji} {cat==="All"?"All Products":cat}
        </div>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(22px,4vw,40px)", fontWeight:900, color:"white" }}>
          {cat==="All" ? "Shop Everything" : cat}
        </h1>
      </div>

      {/* Search + Filter bar */}
      <div className="shop-stickybar" style={{ background:"white", padding:"12px clamp(12px,3vw,32px)", borderBottom:"1px solid #f0f0f0", display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", position:"sticky", zIndex:100, boxShadow:"0 2px 12px rgba(0,0,0,0.04)", transform:barHidden?"translateY(-140%)":"translateY(0)", transition:"transform 0.35s cubic-bezier(0.4,0,0.2,1)" }}>
        <div style={{ flex:1, minWidth:180, position:"relative" }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products..." style={{ width:"100%", padding:"9px 16px 9px 38px", border:"1px solid #e5e5e5", borderRadius:50, fontSize:13, outline:"none", background:"#fafafa" }}/>
          <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#999" }}>🔍</span>
        </div>
        <select value={sort} onChange={e=>setSort(e.target.value)} style={{ padding:"9px 14px", border:"1px solid #e5e5e5", borderRadius:50, fontSize:12, outline:"none", background:"#fafafa", cursor:"pointer" }}>
          {["Relevance","Price: Low to High","Price: High to Low","Top Rated"].map(o=><option key={o}>{o}</option>)}
        </select>
        <button onClick={()=>setShowF(!showF)} style={{ padding:"9px 18px", background:showF?"#1a1a2e":"white", color:showF?"white":"#1a1a2e", border:"1px solid #1a1a2e", borderRadius:50, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          ☰ Filters
        </button>
      </div>

      <div style={{ display:"flex", minHeight:"70vh" }}>
        {/* Sidebar */}
        {showF && (
          <div className="shop-sidebar" style={{ width:220, flexShrink:0, background:"white", padding:20, borderRight:"1px solid #f0f0f0", position:"sticky", overflowY:"auto" }}>
            <div style={{ fontWeight:800, fontSize:15, color:"#1a1a2e", marginBottom:16 }}>Filters</div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"#999", fontWeight:700, marginBottom:10 }}>Category</div>
              {["All",...categories].map(c=>{
                const m = c!=="All" ? getMeta(c) : null;
                return (
                  <div key={c} onClick={()=>setCat(c)}
                    style={{ padding:"8px 10px", cursor:"pointer", fontSize:13, color:cat===c?(m?.color||"#EC4899"):"#666", background:cat===c?(m?.light||"#FDF2F8"):"transparent", borderRadius:8, fontWeight:cat===c?700:400, marginBottom:2, display:"flex", alignItems:"center", gap:6, transition:"all 0.2s" }}>
                    {m && <span>{m.emoji}</span>}{c}
                  </div>
                );
              })}
            </div>
            <div>
              <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"#999", fontWeight:700, marginBottom:6 }}>Max Price: ₹{maxP.toLocaleString("en-IN")}</div>
              <input type="range" min={0} max={15000} step={100} value={maxP} onChange={e=>setMaxP(+e.target.value)} style={{ width:"100%", accentColor:"#EC4899" }}/>
            </div>

            {allSizes.length>0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"#999", fontWeight:700, marginBottom:10 }}>Size</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {allSizes.map(sz=>(
                    <button key={sz} onClick={()=>toggleFilter(selSizes,setSelSizes,sz)}
                      style={{ padding:"5px 12px", fontSize:11, border:`1.5px solid ${selSizes.includes(sz)?"#EC4899":"#e5e5e5"}`, background:selSizes.includes(sz)?"#FDF2F8":"white", color:selSizes.includes(sz)?"#EC4899":"#666", borderRadius:8, cursor:"pointer", fontWeight:600 }}>
                      {sz}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {allColors.length>0 && (
              <div>
                <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"#999", fontWeight:700, marginBottom:10 }}>Color</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {allColors.map(c=>(
                    <button key={c} onClick={()=>toggleFilter(selColors,setSelColors,c)}
                      style={{ padding:"5px 12px", fontSize:11, border:`1.5px solid ${selColors.includes(c)?"#EC4899":"#e5e5e5"}`, background:selColors.includes(c)?"#FDF2F8":"white", color:selColors.includes(c)?"#EC4899":"#666", borderRadius:8, cursor:"pointer", fontWeight:600 }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Grid */}
        <div style={{ flex:1, padding:"20px clamp(12px,3vw,28px)" }}>
          {(() => {
            const catVids = videoShowcases.filter(v => v.active !== false && v.placement === `shop:${cat}`);
            if (!catVids.length) return null;
            const bannerVids = catVids.filter(v=>v.aspectRatio==="banner");
            const otherVids = catVids.filter(v=>v.aspectRatio!=="banner");
            return (
              <>
                {bannerVids.map(v=>(
                  <div key={v.id} style={{ lineHeight:0, marginBottom:18, marginLeft:"calc(-1 * clamp(12px,3vw,28px))", width:"calc(100% + 2 * clamp(12px,3vw,28px))" }}>
                    <VideoProductCard video={v} product={products.find(p=>p.id===v.productId)} onNav={onNav}/>
                  </div>
                ))}
                {otherVids.length>0 && (
                  <div style={{ display:"flex", gap:14, overflowX:"auto", marginBottom:18, scrollbarWidth:"none" }}>
                    {otherVids.map(v=>(
                      <VideoProductCard key={v.id} video={v} product={products.find(p=>p.id===v.productId)} onNav={onNav}/>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          <div style={{ marginBottom:14, fontSize:12, color:"#999", fontWeight:600 }}>{filtered.length} products found</div>
          {filtered.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
              <p style={{ color:"#999" }}>No products found. Try different filters.</p>
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(clamp(140px,22vw,220px),1fr))", gap:"clamp(10px,2vw,18px)" }}>
              {filtered.map(p=><ProductCard key={p.id} p={p} onNav={onNav}/>)}
            </div>
          )}
        </div>
      </div>
      <Footer onNav={onNav}/>
    </div>
  );
}
